import {
  WORK_ORDER_CATEGORY_LABELS,
  WORK_ORDER_SEVERITY_LABELS,
  type WorkOrderCategory,
  type WorkOrderSeverity,
} from '@sitewise/shared';
import { Button, toast } from '@sitewise/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';

import { acceptAiSuggestion, dismissAiSuggestion } from '../api/ai.js';
import { ApiError } from '../lib/api-client.js';

interface AiSuggestionChipProps {
  workOrderId: string;
  /** Current canonical category (so we can hide redundant suggestions). */
  currentCategory: string;
  currentSeverity: string;
  suggestion: {
    category: string | null;
    severity: string | null;
    confidence: number | null;
    rationale: string | null;
    suggestedAt: string | null;
  };
  /** Optional callback after accept/dismiss completes. */
  onChanged?: () => void;
}

/**
 * Phase 8 — AI suggestion chip rendered inside the WO detail page.
 * Shown only when at least one suggestion field differs from the
 * canonical fields. "Accept" applies the suggestion to the canonical
 * columns and clears the suggestion; "Dismiss" clears it without
 * touching the canonical state.
 */
export function AiSuggestionChip({
  workOrderId,
  currentCategory,
  currentSeverity,
  suggestion,
  onChanged,
}: AiSuggestionChipProps) {
  const queryClient = useQueryClient();
  const acceptMutation = useMutation({
    mutationFn: () =>
      acceptAiSuggestion(workOrderId, {
        category: (suggestion.category as WorkOrderCategory | null) ?? undefined,
        severity: (suggestion.severity as WorkOrderSeverity | null) ?? undefined,
      }),
    onSuccess: () => {
      toast.success('Applied AI suggestion');
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] });
      onChanged?.();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not apply'),
  });

  const dismissMutation = useMutation({
    mutationFn: () => dismissAiSuggestion(workOrderId),
    onSuccess: () => {
      toast.success('Dismissed AI suggestion');
      void queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] });
      onChanged?.();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Could not dismiss'),
  });

  if (!suggestion.suggestedAt) return null;

  // If the suggestion already matches canonical state, don't show it.
  const sameCategory = suggestion.category === null || suggestion.category === currentCategory;
  const sameSeverity = suggestion.severity === null || suggestion.severity === currentSeverity;
  if (sameCategory && sameSeverity) return null;

  const categoryLabel =
    suggestion.category &&
    WORK_ORDER_CATEGORY_LABELS[suggestion.category as keyof typeof WORK_ORDER_CATEGORY_LABELS];
  const severityLabel =
    suggestion.severity &&
    WORK_ORDER_SEVERITY_LABELS[suggestion.severity as WorkOrderSeverity];
  const confidencePct =
    suggestion.confidence == null ? null : `${Math.round(suggestion.confidence * 100)}%`;

  const pending = acceptMutation.isPending || dismissMutation.isPending;

  return (
    <div className="flex items-start gap-3 rounded-lg border border-primary/40 bg-primary-subtle/40 px-4 py-3 text-sm">
      <Sparkles className="mt-0.5 h-4 w-4 text-primary" aria-hidden />
      <div className="flex-1">
        <p className="font-medium text-fg">AI suggests:</p>
        <ul className="mt-1 space-y-0.5 text-fg-secondary">
          {!sameCategory && categoryLabel ? (
            <li>
              Category → <span className="font-medium text-fg">{categoryLabel}</span>
            </li>
          ) : null}
          {!sameSeverity && severityLabel ? (
            <li>
              Severity → <span className="font-medium text-fg">{severityLabel}</span>
            </li>
          ) : null}
        </ul>
        {suggestion.rationale ? (
          <p className="mt-1 text-xs text-fg-muted italic">"{suggestion.rationale}"</p>
        ) : null}
        {confidencePct ? (
          <p className="mt-1 text-xs text-fg-muted">Confidence: {confidencePct}</p>
        ) : null}
      </div>
      <div className="flex flex-col gap-2">
        <Button
          size="sm"
          onClick={() => acceptMutation.mutate()}
          disabled={pending}
        >
          Accept
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => dismissMutation.mutate()}
          disabled={pending}
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}
